import React, { useCallback, useEffect, useState, useRef } from "react";
import RichTextEditor from "../../components/richTextEditor/RichTextEditor";
import userService from "../../store/services/user.service";
import searchService from "../../store/services/search.service";
import { generalActions } from "../../store/actions";
import { useDispatch } from "react-redux";
import { debounce } from 'lodash';
import { X, Briefcase, Trash2, ChevronDown, Search, User, Bookmark } from "lucide-react";
import "./MakeSure.scss";
import { AttachementRefType } from "../../store/services/attachments.service";
import InputUnderline from "../../components/controls/input/InputUnderline";

import useNavigation from "../../utils/useNavigation";

const PERSONAL_ID = "personal";
const generateId = () => `ms_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
const isJsonArray = (data) => {
  try { const p = JSON.parse(data); return Array.isArray(p); } catch { return false; }
};

const MakeSure = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [makeSures, setMakeSures] = useState([]);
  const [selectedId, setSelectedId] = useState(PERSONAL_ID);
  const [projectSearch, setProjectSearch] = useState("");
  const [projectResults, setProjectResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [deletePending, setDeletePending] = useState(null); // { id, name, top, left }
  const deletePopoverRef = useRef(null);
  const [selectorOpen, setSelectorOpen] = useState(false);
  const [selectorSearch, setSelectorSearch] = useState("");
  const selectorRef = useRef(null);
  const selectorSearchRef = useRef(null);

  const user = JSON.parse(localStorage.getItem("user"));
  const currentUserId = user.id;
  const dispatch = useDispatch();

  const makeSuresRef = useRef([]);
 const { navigateTo } = useNavigation();

  const handleRedirect = (item) => {
    navigateTo("Projects", item.id, item.name);
  };

  const debouncedSave = useRef(debounce(() => {
    userService.updateUserNotes({ notes: JSON.stringify(makeSuresRef.current) });
  }, 1000));

  const saveList = useCallback((list) => {
    makeSuresRef.current = list;
    setMakeSures(list);
    debouncedSave.current();
  }, []);

  // Initial load
  useEffect(() => {
    setIsLoading(true);
    userService.getUserNotes()
      .then((data) => {
        let list;
        if (isJsonArray(data)) {
          list = JSON.parse(data);
          if (!list.find(m => m.id === PERSONAL_ID)) {
            list.unshift({ id: PERSONAL_ID, name: "Personal", notes: "", projectId: null, projectName: null });
          }
        } else {
          // Legacy plain string — migrate to Personal make sure
          list = [{ id: PERSONAL_ID, name: "Personal", notes: data || "", projectId: null, projectName: null }];
          userService.updateUserNotes({ notes: JSON.stringify(list) });
        }
        makeSuresRef.current = list;
        setMakeSures(list);
      })
      .catch(() => {
        dispatch(generalActions.showToast({ title: "Something went wrong. Please try again later.", type: "error" }));
      })
      .finally(() => setIsLoading(false));
  }, []);

  const selected = makeSures.find(m => m.id === selectedId) || makeSures[0];
  const isPersonal = selected?.id === PERSONAL_ID;

  const sortedMakeSures = [
    ...makeSures.filter(m => m.id === PERSONAL_ID),
    ...makeSures.filter(m => m.id !== PERSONAL_ID && m.bookmarked).sort((a, b) => a.name.localeCompare(b.name)),
    ...makeSures.filter(m => m.id !== PERSONAL_ID && !m.bookmarked).sort((a, b) => a.name.localeCompare(b.name)),
  ];

  // Sync editor content when selection changes
  useEffect(() => {
    setProjectSearch("");
    setProjectResults([]);
  }, [selectedId]);

  useEffect(() => {
    if (!selectorOpen) {
      setSelectorSearch("");
      return;
    }
    const handler = (e) => {
      if (selectorRef.current && !selectorRef.current.contains(e.target))
        setSelectorOpen(false);
    };
    document.addEventListener('mousedown', handler);
    // Auto-focus the search input when dropdown opens
    setTimeout(() => selectorSearchRef.current?.focus(), 0);
    return () => document.removeEventListener('mousedown', handler);
  }, [selectorOpen]);

  const handleSelectChange = (val) => {
    if (val === "new") {
      const newItem = { id: generateId(), name: "New Make Sure", notes: "", projectId: null, projectName: null };
      const updated = [...makeSuresRef.current, newItem];
      saveList(updated);
      setSelectedId(newItem.id);
    } else {
      setSelectedId(val);
    }
  };

  const handleNameChange = useCallback((value) => {
    const updated = makeSuresRef.current.map(m => m.id === selectedId ? { ...m, name: value } : m);
    saveList(updated);
  }, [selectedId, saveList]);

  const handleContentChange = useCallback((value) => {
    const updated = makeSuresRef.current.map(m => m.id === selectedId ? { ...m, notes: value } : m);
    saveList(updated);
  }, [selectedId, saveList]);

  const handleDeleteConfirmed = () => {
    if (!deletePending) return;
    const updated = makeSuresRef.current.filter(m => m.id !== deletePending.id);
    saveList(updated);
    if (selectedId === deletePending.id) setSelectedId(PERSONAL_ID);
    setDeletePending(null);
  };

  // Debounced project search
  const searchProjectDebounced = useRef(debounce((text) => {
    if (!text) { setProjectResults([]); setIsSearching(false); return; }
    searchService.Projects(text)
      .then((res) => setProjectResults(res))
      .catch(() => setProjectResults([]))
      .finally(() => setIsSearching(false));
  }, 500));

  const handleProjectSearchChange = (e) => {
    const val = e.target.value;
    setProjectSearch(val);
    if (val) setIsSearching(true);
    else { setProjectResults([]); setIsSearching(false); }
    searchProjectDebounced.current(val);
  };

  const handleProjectSelect = (project) => {
    const updated = makeSuresRef.current.map(m => m.id === selectedId ? { ...m, projectId: project.id, projectName: project.name } : m);
    saveList(updated);
    setProjectSearch("");
    setProjectResults([]);
  };

  const handleProjectRemove = () => {
    const updated = makeSuresRef.current.map(m => m.id === selectedId ? { ...m, projectId: null, projectName: null } : m);
    saveList(updated);
  };

  const handleToggleBookmark = (id, e) => {
    e.stopPropagation();
    const updated = makeSuresRef.current.map(m => m.id === id ? { ...m, bookmarked: !m.bookmarked } : m);
    saveList(updated);
  };

  return (
    <div className="makeSureDrawerContent">

      {/* Toolbar: Make Sure selector */}
      <div className="makeSure-toolbar">
        <div className="makeSure-selector" ref={selectorRef}>
          <button
            type="button"
            className="makeSure-select-btn"
            onClick={() => setSelectorOpen(v => !v)}
            disabled={isLoading}
          >
            <span className="makeSure-select-value">
              {isLoading ? 'Loading…' : (
                selected?.id === PERSONAL_ID
                  ? <><User size={12} className="makeSure-select-personal-icon" />Personal</>
                  : (selected?.name || 'Select')
              )}
            </span>
            <ChevronDown size={14} className={`makeSure-select-chevron${selectorOpen ? ' open' : ''}`} />
          </button>
          {selectorOpen && (
            <ul className="makeSure-select-list">
              <li className="makeSure-select-sticky-header">
                <div className="makeSure-select-search-item" onMouseDown={(e) => e.preventDefault()}>
                  <Search size={12} className="makeSure-select-search-icon" />
                  <input
                    ref={selectorSearchRef}
                    type="text"
                    className="makeSure-select-search-input"
                    placeholder="Search…"
                    value={selectorSearch}
                    onChange={(e) => setSelectorSearch(e.target.value)}
                  />
                  {selectorSearch && (
                    <button type="button" className="makeSure-select-search-clear" onClick={() => setSelectorSearch("")}>
                      <X size={10} />
                    </button>
                  )}
                </div>
                <div
                  className="makeSure-select-item makeSure-select-item--new"
                  onClick={() => { setSelectorOpen(false); setSelectorSearch(""); handleSelectChange('new'); }}
                >
                  + Add New
                </div>
              </li>
              {sortedMakeSures.filter(m =>
                !selectorSearch ||
                (m.id === PERSONAL_ID ? 'personal' : m.name.toLowerCase()).includes(selectorSearch.toLowerCase())
              ).map(m => (
                <li
                  key={m.id}
                  className={`makeSure-select-item${m.id === selectedId ? ' active' : ''}${m.id === PERSONAL_ID ? ' makeSure-select-item--personal' : ''}`}
                >
                  {m.id === PERSONAL_ID ? (
                    <User size={12} className="makeSure-select-personal-badge" />
                  ) : (
                    <button
                      type="button"
                      className={`makeSure-select-bookmark${m.bookmarked ? ' bookmarked' : ''}`}
                      title={m.bookmarked ? 'Remove bookmark' : 'Bookmark'}
                      onClick={(e) => handleToggleBookmark(m.id, e)}
                    >
                      <Bookmark size={12} />
                    </button>
                  )}
                  <span
                    className="makeSure-select-item-label"
                    onClick={() => { setSelectorOpen(false); handleSelectChange(m.id); }}
                  >
                    {m.id === PERSONAL_ID ? 'Personal' : m.name}
                  </span>
                  {m.id !== PERSONAL_ID && (
                    <button
                      type="button"
                      className="makeSure-select-delete"
                      title="Delete"
                      onClick={(e) => { e.stopPropagation(); const r = e.currentTarget.getBoundingClientRect(); setSelectorOpen(false); setSelectorSearch(""); setDeletePending({ id: m.id, name: m.name, top: r.bottom + 6, left: r.right - 220 }); }}
                    >
                      <Trash2 size={12} />
                    </button>
                  )}
                </li>
              ))}

            </ul>
          )}
        </div>
      </div>

      {selected && (
        <div className="makeSure-body">
          {/* Name field for non-personal */}
          {!isPersonal && (
            <div className="makeSure-meta-row">
              <InputUnderline
                value={selected.name}
                handleChange={handleNameChange}
                placeholder="Make Sure name"
                loading={isLoading}
                autoFocus={false}
              />
            </div>
          )}

          {/* Project linker */}
          {!isPersonal && (
            <div className="makeSure-project">
              {selected.projectId ? (
                <div className="makeSure-project-linked">
                  <Briefcase size={13} className="makeSure-project-icon" />
                  <span 
                    className="makeSure-project-name"
                    onClick={() => handleRedirect({ id: selected.projectId, name: selected.projectName })}
                    >    
                    {selected.projectName}
                  </span>
                  <button
                    className="makeSure-project-remove"
                    onClick={handleProjectRemove}
                    title="Unlink project"
                  >
                    <X size={12} />
                  </button>
                </div>
              ) : (
                <div className="makeSure-project-search-wrap">
                  <Briefcase size={13} className="makeSure-project-search-icon" />
                  <input
                    type="text"
                    className="makeSure-project-input"
                    placeholder="Link to a project (optional)"
                    value={projectSearch}
                    onChange={handleProjectSearchChange}
                  />
                  {isSearching && (
                    <span className="makeSure-searching">
                      <span className="makeSure-spinner" />
                    </span>
                  )}
                  {projectResults.length > 0 && (
                    <ul className="makeSure-project-results">
                      {projectResults.map(p => (
                        <li key={p.id} onClick={() => handleProjectSelect(p)}>
                          <Briefcase size={12} />
                          <span>{p.name}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Editor */}
          <div className="makeSure-editor">
            <RichTextEditor
              key={selectedId}
              mode="advanced"
              content={selected.notes || ""}
              onChange={handleContentChange}
              fileUploadParams={{
                enabled: true,
                refType: AttachementRefType.MakeSure,
                refId: currentUserId,
              }}
              loading={isLoading}
            />
          </div>
        </div>
      )}

      {deletePending && (
        <>
          <div className="makeSure-delete-backdrop" onClick={() => setDeletePending(null)} />
          <div
            ref={deletePopoverRef}
            className="makeSure-delete-popover"
            style={{ top: deletePending.top, left: Math.max(8, deletePending.left) }}
          >
            <p className="makeSure-delete-popover-msg">
              Delete <b>{deletePending.name}</b>?
            </p>
            <div className="makeSure-delete-popover-actions">
              <button className="makeSure-delete-popover-cancel" onClick={() => setDeletePending(null)}>Cancel</button>
              <button className="makeSure-delete-popover-confirm" onClick={handleDeleteConfirmed}>Delete</button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default MakeSure;